import { useNavigate, useSearchParams } from "react-router-dom";
import { BarChart3, History, Sparkles, Workflow as WorkflowIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { withWorkspaceContext } from "@/lib/workspace-navigation";

/**
 * Canvas / Runs / Evaluation / Insights switcher for a workflow, the same on
 * all four of its pages. Canvas joined on 2026-10-02: the canvas had a lone
 * "Runs" button instead, so getting around depended on which page you were on.
 *
 * These are two different kinds of reading. The runs list is a scannable
 * table you come to with a specific run in mind; an evaluation is a page of
 * prose you sit and read. Stacking the second above the first pushed the
 * table below the fold and made both worse, so they get their own routes.
 */
export type WorkflowTab = "canvas" | "runs" | "evaluation" | "insights";

export function WorkflowTabs({
  tenantId,
  workflowId,
  active,
}: {
  tenantId: string;
  workflowId: string;
  active: WorkflowTab;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const go = (tab: WorkflowTab) => {
    if (tab === active) return;
    // The canvas is the workflow's own address; the others hang off it.
    const suffix = tab === "canvas" ? "" : `/${tab}`;
    navigate(
      withWorkspaceContext(
        `/tenants/${tenantId}/workflows/${workflowId}${suffix}`,
        searchParams,
      ),
    );
  };

  const tabs = [
    { id: "canvas" as const, label: "Canvas", Icon: WorkflowIcon },
    { id: "runs" as const, label: "Runs", Icon: History },
    { id: "evaluation" as const, label: "Evaluation", Icon: Sparkles },
    { id: "insights" as const, label: "Insights", Icon: BarChart3 },
  ];

  return (
    <div className="flex items-center gap-0.5">
      {tabs.map(({ id, label, Icon }) => (
        <button
          key={id}
          onClick={() => go(id)}
          className={cn(
            "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
            active === id
              ? "bg-surface-hover text-text-primary"
              : "text-text-muted hover:bg-surface-hover hover:text-text-primary",
          )}
        >
          <Icon size={13} />
          {label}
        </button>
      ))}
    </div>
  );
}
